/**
 * Service for handling Google Analytics tracking
 * Measurement ID: G-ZDBD03LPEX
 */

// Track last page view to prevent duplicates
let lastPageView = {
  path: null,
  title: null,
  timestamp: 0
};

// Debounce time for page views (in milliseconds)
const PAGE_VIEW_DEBOUNCE = 1000;

const analyticsService = {
  /**
   * Track a page view
   * @param {string} path - The page path to track
   * @param {string} title - The page title
   */
  trackPageView(path, title) {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      // Normalize title by removing trademark symbol and trimming
      const normalizedTitle = (title || '').replace(/™/g, '').trim();
      
      // Check for duplicate page view within debounce window
      const now = Date.now();
      const isDuplicate = 
        lastPageView.path === path && 
        lastPageView.title === normalizedTitle &&
        (now - lastPageView.timestamp) < PAGE_VIEW_DEBOUNCE;
      
      if (!isDuplicate) {
        // Update Google Analytics
        window.gtag('config', 'G-ZDBD03LPEX', {
          page_path: path,
          page_title: normalizedTitle,
        });
        
        // Update last page view tracker
        lastPageView = {
          path,
          title: normalizedTitle,
          timestamp: now
        };
      }
    }
  },

  /**
   * Track an event
   * @param {string} eventName - Name of the event to track
   * @param {Object} eventParams - Parameters for the event
   */
  trackEvent(eventName, eventParams = {}) {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, eventParams);
    }
  },

  /**
   * Track a user sign-up
   * @param {string} method - The sign-up method (email, google, etc.)
   */
  trackSignUp(method = 'email') {
    this.trackEvent('sign_up', { method });
  },

  /**
   * Track a user login
   * @param {string} method - The login method (email, google, etc.)
   */
  trackLogin(method = 'email') {
    this.trackEvent('login', { method });
  },

  /**
   * Track a subscription
   * @param {string} plan - The subscription plan
   * @param {number} value - The monetary value
   */
  trackSubscription(plan, value) {
    this.trackEvent('subscription', { 
      plan,
      value,
      currency: 'USD'
    });
  },

  /**
   * Track a purchase
   * @param {string} id - Transaction ID
   * @param {number} value - Purchase value
   * @param {Array} items - Items purchased
   */
  trackPurchase(id, value, items = []) {
    this.trackEvent('purchase', {
      transaction_id: id,
      value,
      currency: 'USD',
      items
    });
  },

  /**
   * Track a form submission
   * @param {string} formName - Name of the form
   * @param {string} formId - ID of the form
   */
  trackFormSubmission(formName, formId) {
    this.trackEvent('form_submit', {
      form_name: formName,
      form_id: formId
    });
  },

  /**
   * Track video playback
   * @param {string} videoTitle - Title of the video
   * @param {string} videoId - ID of the video
   * @param {string} action - Playback action (play, pause, complete)
   */
  trackVideoPlayback(videoTitle, videoId, action) {
    this.trackEvent('video_playback', {
      video_title: videoTitle,
      video_id: videoId,
      action
    });
  },

  /**
   * Track a search query
   * @param {string} query - The search query
   * @param {string} category - The search category
   */
  trackSearch(query, category) {
    this.trackEvent('search', {
      search_term: query,
      category
    });
  },

  /**
   * Track an error
   * @param {string} errorType - Type of error
   * @param {string} errorMessage - Error message
   */
  trackError(errorType, errorMessage) {
    this.trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage
    });
  }
};

export default analyticsService; 